import { Helmet } from "react-helmet-async";

const SITE_NAME = "Checkout DM";
const SITE_URL = "https://checkoutdm.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = "@checkoutdm";

/**
 * Reusable per-page SEO component.
 *
 * Usage:
 *   <SEO
 *     title="Pricing | Checkout DM"
 *     description="Plans for Instagram DM commerce, starting free."
 *     path="/pricing"
 *   />
 *
 * Pass `noindex` for private/app routes (dashboard, login, billing, etc).
 * Pass `structuredData` (object or array of objects) to inject JSON-LD.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  structuredData = null,
  type = "website",
}) {
  const url = `${SITE_URL}${path === "/" ? "" : path}`;
  const jsonLdList = Array.isArray(structuredData)
    ? structuredData
    : structuredData
    ? [structuredData]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLdList.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE, TWITTER_HANDLE };
